import type { ProjectReviewStatus } from '@dyeatlas/shared'

export type WorkspaceRole = 'owner' | 'member'

export interface WorkspaceSummary {
  id: string
  name: string
  description: string | null
  ownerId: string
  role: WorkspaceRole
  isOwner: boolean
  createdAt: string
  recordCount: number
  memberCount: number
  reviewStatus: ProjectReviewStatus
}

export interface WorkspaceMember {
  id: string
  name: string
  email: string
  role: WorkspaceRole
  joinedAt: string
}

export interface WorkspaceDetail {
  workspace: WorkspaceSummary & {
    pendingInviteCount: number
    submittedAt: string | null
    publishedAt: string | null
    rejectionReason: string | null
  }
  members: WorkspaceMember[]
  memberPreview: WorkspaceMember[]
}

export interface WorkspaceRecord {
  id: string
  code: string
  kind: 'experiment' | 'reference'
  status: string
  title: string
  swatchHex: string | null
  resLabL: number | string | null
  resLabA: number | string | null
  resLabB: number | string | null
  researcherName: string | null
  experimentDate: string | null
  referenceYear: number | null
  dyeSourceName: string | null
  fiberName: string | null
  addedAt: string
  addedByName: string
  publishedCode: string | null
}

export interface PendingInvite {
  id: string
  email: string
  expiresAt: string
  createdAt: string
}

/** Initials for the avatar circles, from a display name or an email address. */
export function initialsOf(member: { name: string | null; email: string }): string {
  const source = member.name?.trim() || member.email
  const parts = source.split(/[\s.@_-]+/).filter(Boolean)
  return (parts[0]?.[0] ?? '?').concat(parts[1]?.[0] ?? '').toUpperCase()
}

/**
 * A stable colour per member, so the same person keeps the same avatar everywhere.
 * Hues are spaced around the wheel and muted to sit against the paper palette.
 */
export function avatarColour(seed: string): string {
  let hash = 0
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) % 360
  }
  return `hsl(${hash} 28% 46%)`
}
